<template>
  <div>
    <headerSection></headerSection>
    <div class="history-page">
      <!-- Hero Section -->
      <div class="history-hero">
        <div class="hero-bg-effects">
          <div class="floating-ball ball-1"></div>
          <div class="floating-ball ball-2"></div>
          <div class="floating-ball ball-3"></div>
        </div>
        <div class="container">
          <div class="hero-content">
            <div class="hero-icon">📜</div>
            <h1 class="hero-title">
              <span class="title-glow">{{ $t('history_page.title') }}</span>
            </h1>
            <p class="hero-description">
              {{ $t('history_page.hero_description') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="container history-content">
        <!-- Stats Summary -->
        <div class="stats-summary" v-if="history.length > 0 && !loading">
          <div class="summary-item">
            <span class="summary-icon">📊</span>
            <span class="summary-value">{{ history.length }}</span>
            <span class="summary-label">{{ $t('history_page.summary_label') }}</span>
          </div>
        </div>

        <!-- Loading State -->
        <div class="loading-state" v-if="loading">
          <div class="loading-spinner"></div>
          <div class="loading-text">{{ $t('loading') }}</div>
        </div>

        <!-- Empty State -->
        <div class="empty-state" v-else-if="history.length === 0">
          <div class="empty-icon">📭</div>
          <div class="empty-text">{{ $t('history_page.no_data') }}</div>
          <p class="empty-subtext">{{ $t('history_page.empty_subtext') }}</p>
        </div>

        <!-- Transaction List -->
        <div class="transaction-list" v-else>
          <div
            v-for="(item, index) in history"
            :key="index"
            :class="['transaction-card', getTypeClass(item.type)]"
            :style="{ animationDelay: `${Math.min(index * 0.05, 0.5)}s` }"
          >
            <!-- Left Section - Type Icon -->
            <div class="transaction-icon-wrapper" :class="getTypeClass(item.type)">
              <span class="transaction-icon">{{ getTypeIcon(item.type) }}</span>
            </div>

            <!-- Middle Section - Details -->
            <div class="transaction-details">
              <div class="transaction-type">{{ item.type }}</div>
              <div class="transaction-date">{{ item.createdateShow }}</div>
            </div>

            <!-- Right Section - Amount & Status -->
            <div class="transaction-amount-section">
              <div :class="['transaction-amount', getAmountClass(item.type)]">
                {{ getAmountPrefix(item.type) }}{{ numberWithCommas(item.qty) }}
              </div>
              <div :class="['transaction-status', getStatusClass(item.status)]">
                <span class="status-icon">{{ getStatusIcon(item.status) }}</span>
                <span class="status-text">{{ getDisplayStatus(item.status) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <footerSection></footerSection>
  </div>
</template>

<script>
import firebase from 'firebase/app'
import 'firebase/database'
import router from './../router'
import moment from 'moment-timezone'
import _ from 'lodash'

export default {
  name: 'History',
  components: {
    headerSection: () => import('./Header.vue'),
    footerSection: () => import('./Footer.vue')
  },
  computed: {
    user() {
      return this.$store.state.user.user
    }
  },
  data() {
    return {
      history: [],
      loading: true
    }
  },
  methods: {
    scrollToTop() {
      window.scrollTo(0, 0)
    },
    numberWithCommas(x) {
      if (!x) x = 0
      return Number(x).toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    getDisplayStatus(status) {
      if (status === 'ดำเนินการแล้ว') return this.$t('history_page.status_success')
      if (status === 'ยกเลิก') return this.$t('history_page.status_cancelled')
      return this.$t('history_page.status_pending')
    },
    getTypeIcon(type) {
      const depositLabel = this.$t('history_page.type_deposit')
      const withdrawLabel = this.$t('history_page.type_withdraw')
      const wheelLabel = this.$t('history_page.type_wheel')
      const rouletteLabel = this.$t('history_page.type_roulette')
      const continueLabel = this.$t('history_page.type_continue')
      const referralLabel = this.$t('history_page.type_referral')
      const cashbackLabel = this.$t('history_page.type_cashback')

      const icons = {
        [depositLabel]: '📥',
        [withdrawLabel]: '📤',
        [wheelLabel]: '🎡',
        [rouletteLabel]: '🎰',
        [continueLabel]: '🔥',
        [referralLabel]: '👥',
        [cashbackLabel]: '💎',
        // Legacy support for Thai
        'ฝาก': '📥',
        'ถอน': '📤',
        'วงล้อ': '🎡',
        'รูเล็ต': '🎰',
        'ฝากต่อเนื่อง': '🔥',
        'พันธมิตร': '👥',
        'คืนยอดเล่น': '💎'
      }
      return icons[type] || '💰'
    },
    getTypeClass(type) {
      const depositLabel = this.$t('history_page.type_deposit')
      const withdrawLabel = this.$t('history_page.type_withdraw')
      const wheelLabel = this.$t('history_page.type_wheel')
      const rouletteLabel = this.$t('history_page.type_roulette')
      const continueLabel = this.$t('history_page.type_continue')
      const referralLabel = this.$t('history_page.type_referral')
      const cashbackLabel = this.$t('history_page.type_cashback')

      const classes = {
        [depositLabel]: 'deposit',
        [withdrawLabel]: 'withdraw',
        [wheelLabel]: 'bonus',
        [rouletteLabel]: 'bonus',
        [continueLabel]: 'bonus',
        [referralLabel]: 'bonus',
        [cashbackLabel]: 'bonus',
        // Legacy support for Thai
        'ฝาก': 'deposit',
        'ถอน': 'withdraw',
        'วงล้อ': 'bonus',
        'รูเล็ต': 'bonus',
        'ฝากต่อเนื่อง': 'bonus',
        'พันธมิตร': 'bonus',
        'คืนยอดเล่น': 'bonus'
      }
      return classes[type] || 'default'
    },
    getAmountClass(type) {
      const withdrawLabel = this.$t('history_page.type_withdraw')
      if (type === 'ถอน' || type === withdrawLabel) return 'negative'
      return 'positive'
    },
    getAmountPrefix(type) {
      const withdrawLabel = this.$t('history_page.type_withdraw')
      if (type === 'ถอน' || type === withdrawLabel) return '-'
      return '+'
    },
    getStatusIcon(status) {
      if (status === 'ดำเนินการแล้ว') return '✅'
      if (status === 'ยกเลิก') return '❌'
      return '⏳'
    },
    getStatusClass(status) {
      if (status === 'ดำเนินการแล้ว') return 'success'
      if (status === 'ยกเลิก') return 'cancelled'
      return 'pending'
    },
    getUserId() {
      const user = localStorage.getItem('userData')
      if (user) {
        const _user = JSON.parse(user)
        return _user
      } else {
        firebase.auth().signOut()
        localStorage.removeItem('userData')
        router.replace('/login')
      }
    }
  },
  async mounted() {
    this.scrollToTop()
    this.loading = true
    this.$store.commit('user/SET_LOADING', true)

    const user = this.getUserId()
    const history = []
    let lastdate = ''

    try {
    await firebase.firestore().collection('history').where('userkey', '==', user.key).orderBy('createdate', 'desc').limit(500).get().then((snapshot) => {
      snapshot.forEach((doc) => {
        if (doc.data().qty) {
          const data = doc.data()
          data.key = doc.id
          data.createdateShow = moment(data.createdate, 'YYYY-MM-DD HH:mm:ss').format('DD MMM,YY HH:mm')
          lastdate = data.createdate
          history.push(data)
        }
      })
    })

    await firebase.firestore().collection('spin').where('userkey', '==', user.key).where('createdate', '>=', lastdate).get().then((result) => {
      result.forEach((doc) => {
        history.push({
          key: doc.id,
            type: (doc.data().game) ? doc.data().game : 'วงล้อ',
          createdate: doc.data().createdate,
          createdateShow: moment(doc.data().createdate, 'YYYY-MM-DD HH:mm:ss').format('DD MMM,YY HH:mm'),
          qty: doc.data().value,
          status: 'ดำเนินการแล้ว'
        })
      })
    })

    await firebase.firestore().collection('roulet').where('userkey', '==', user.key).where('createdate', '>=', lastdate).get().then((result) => {
      result.forEach((doc) => {
        history.push({
          key: doc.id,
          type: 'รูเล็ต',
          createdate: doc.data().createdate,
          createdateShow: moment(doc.data().createdate, 'YYYY-MM-DD HH:mm:ss').format('DD MMM,YY HH:mm'),
          qty: doc.data().value,
          status: 'ดำเนินการแล้ว'
        })
      })
    })

    await firebase.firestore().collection('continuous_deposit_all').where('userkey', '==', user.key).where('status', '==', true).where('receivedate', '>=', lastdate).get().then((result) => {
      result.forEach((doc) => {
        history.push({
          key: doc.id,
          type: 'ฝากต่อเนื่อง',
          createdate: doc.data().receivedate,
          createdateShow: moment(doc.data().receivedate, 'YYYY-MM-DD HH:mm:ss').format('DD MMM,YY HH:mm'),
          qty: doc.data().total,
          status: 'ดำเนินการแล้ว'
        })
      })
    })

    await firebase.firestore().collection('partner_all').where('userkey', '==', user.key).where('status', '==', true).where('receivedate', '>=', lastdate).get().then((result) => {
      result.forEach((doc) => {
        history.push({
          key: doc.id,
          type: 'พันธมิตร',
          createdate: doc.data().receivedate,
          createdateShow: moment(doc.data().receivedate, 'YYYY-MM-DD HH:mm:ss').format('DD MMM,YY HH:mm'),
          qty: doc.data().total,
          status: 'ดำเนินการแล้ว'
        })
      })
    })

    await firebase.firestore().collection('cashback_all').where('userkey', '==', user.key).where('status', '==', true).where('receivedate', '>=', lastdate).get().then((result) => {
      result.forEach((doc) => {
        history.push({
          key: doc.id,
          type: 'คืนยอดเล่น',
          createdate: doc.data().receivedate,
          createdateShow: moment(doc.data().receivedate, 'YYYY-MM-DD HH:mm:ss').format('DD MMM,YY HH:mm'),
          qty: doc.data().total,
          status: 'ดำเนินการแล้ว'
        })
      })
    })

    this.history = _.orderBy(history, ['createdate'], ['desc'])
    } catch (error) {
      console.error('Error loading history:', error)
    } finally {
      this.loading = false
    this.$store.commit('user/SET_LOADING', false)
    }
  }
}
</script>

<style scoped>
/* ============================================
   HISTORY PAGE - MODERN DESIGN
   ============================================ */

.history-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #1b1b41 0%, #492666 50%, #2b2727 100%);
  padding-bottom: 100px;
}

/* ============================================
   HERO SECTION
   ============================================ */

.history-hero {
  position: relative;
  padding: 40px 0 30px;
  overflow: hidden;
  background: linear-gradient(180deg,
    rgba(131, 9, 162, 0.1) 0%,
    transparent 100%
  );
}

.hero-bg-effects {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
}

.floating-ball {
  position: absolute;
  border-radius: 50%;
  opacity: 0.15;
}

.ball-1 {
  width: 200px;
  height: 200px;
  background: linear-gradient(135deg, #8309a2, #ffc107);
  top: -50px;
  left: -50px;
  animation: float1 6s ease-in-out infinite;
}

.ball-2 {
  width: 150px;
  height: 150px;
  background: linear-gradient(135deg, #fe8a02, #ffc107);
  top: 50%;
  right: -30px;
  animation: float2 8s ease-in-out infinite;
}

.ball-3 {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #492666, #8309a2);
  bottom: -20px;
  left: 30%;
  animation: float3 7s ease-in-out infinite;
}

@keyframes float1 {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  50% { transform: translate(30px, 20px) rotate(180deg); }
}

@keyframes float2 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-20px, -30px) scale(1.1); }
}

@keyframes float3 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(20px, -20px); }
}

.hero-content {
  text-align: center;
  position: relative;
  z-index: 1;
}

.hero-icon {
  font-size: 60px;
  margin-bottom: 15px;
  animation: unroll 2s ease-in-out infinite;
}

@keyframes unroll {
  0%, 100% { transform: rotate(-5deg) scale(1); }
  50% { transform: rotate(5deg) scale(1.05); }
}

.hero-title {
  margin: 0 0 15px;
}

.title-glow {
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(135deg, #ce93d8, #8309a2, #ce93d8);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: purpleShine 3s linear infinite;
}

@keyframes purpleShine {
  0% { background-position: 0% center; }
  100% { background-position: 200% center; }
}

.hero-description {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
}

/* ============================================
   HISTORY CONTENT
   ============================================ */

.history-content {
  padding: 20px 15px;
}

/* ============================================
   STATS SUMMARY
   ============================================ */

.stats-summary {
  display: flex;
  justify-content: center;
  margin-bottom: 25px;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, rgba(131, 9, 162, 0.2), rgba(73, 38, 102, 0.1));
  border: 1px solid rgba(131, 9, 162, 0.3);
  border-radius: 50px;
  padding: 10px 25px;
}

.summary-icon {
  font-size: 20px;
}

.summary-value {
  font-size: 20px;
  font-weight: 700;
  color: #ce93d8;
}

.summary-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
}

/* ============================================
   TRANSACTION LIST
   ============================================ */

.transaction-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.transaction-card {
  display: flex;
  align-items: center;
  gap: 15px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 15px;
  transition: all 0.3s ease;
  animation: slideIn 0.4s ease-out backwards;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.transaction-card:hover {
  transform: translateX(5px);
  border-color: rgba(255, 255, 255, 0.2);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
}

.transaction-card.deposit {
  border-left: 3px solid #4caf50;
}

.transaction-card.withdraw {
  border-left: 3px solid #f44336;
}

.transaction-card.bonus {
  border-left: 3px solid #2196f3;
}

/* Transaction Icon */
.transaction-icon-wrapper {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.transaction-icon-wrapper.deposit {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.2), rgba(139, 195, 74, 0.1));
}

.transaction-icon-wrapper.withdraw {
  background: linear-gradient(135deg, rgba(244, 67, 54, 0.2), rgba(229, 115, 115, 0.1));
}

.transaction-icon-wrapper.bonus {
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.2), rgba(3, 169, 244, 0.1));
}

.transaction-icon {
  font-size: 24px;
}

/* Transaction Details */
.transaction-details {
  flex: 1;
  min-width: 0;
}

.transaction-type {
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 4px;
}

.transaction-date {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}

/* Transaction Amount Section */
.transaction-amount-section {
  text-align: right;
  flex-shrink: 0;
}

.transaction-amount {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 4px;
}

.transaction-amount.positive {
  color: #4caf50;
}

.transaction-amount.negative {
  color: #f44336;
}

.transaction-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 20px;
}

.transaction-status.success {
  background: rgba(76, 175, 80, 0.15);
  color: #4caf50;
}

.transaction-status.cancelled {
  background: rgba(244, 67, 54, 0.15);
  color: #f44336;
}

.transaction-status.pending {
  background: rgba(255, 193, 7, 0.15);
  color: #ffc107;
}

.status-icon {
  font-size: 10px;
}

.status-text {
  font-size: 10px;
}

/* ============================================
   EMPTY STATE
   ============================================ */

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.5;
}

.empty-text {
  font-size: 18px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 10px;
}

.empty-subtext {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
}

/* ============================================
   LOADING STATE
   ============================================ */

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #ce93d8;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
}

/* ============================================
   RESPONSIVE
   ============================================ */

@media (max-width: 480px) {
  .hero-icon {
    font-size: 48px;
  }

  .title-glow {
    font-size: 24px;
  }

  .transaction-card {
    padding: 12px;
    gap: 12px;
  }

  .transaction-icon-wrapper {
    width: 42px;
    height: 42px;
  }

  .transaction-icon {
    font-size: 20px;
  }

  .transaction-type {
    font-size: 14px;
  }

  .transaction-amount {
    font-size: 14px;
  }

  .summary-item {
    padding: 8px 18px;
  }

  .summary-value {
    font-size: 18px;
  }
}

@media (min-width: 769px) {
  .history-content {
    max-width: 600px;
    margin: 0 auto;
  }

  .title-glow {
    font-size: 32px;
  }
}
</style>
